import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { showNotification } from '@mantine/notifications'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'

type BookmarksContextValue = {
  bookmarkedIds: ReadonlySet<string>
  loading: boolean
  /** Увеличивается после успешного add/remove — для перезагрузки страницы «Закладки» */
  listRevision: number
  refresh: () => Promise<void>
  clearAll: (projectIds?: string[] | null, search?: string) => Promise<void>
  clearMany: (ticketIds: string[]) => Promise<void>
  isBookmarked: (ticketId: string) => boolean
  toggleBookmark: (ticketId: string) => Promise<void>
}

const BookmarksContext = createContext<BookmarksContextValue | null>(null)

export function BookmarksProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(() => new Set())
  const [listRevision, setListRevision] = useState(0)
  const [loading, setLoading] = useState(false)
  const idsRef = useRef(bookmarkedIds)
  idsRef.current = bookmarkedIds

  const refresh = useCallback(async () => {
    if (!user) {
      setBookmarkedIds(new Set())
      return
    }
    setLoading(true)
    try {
      const r = await api.bookmarksList({ idsOnly: true })
      const next = new Set<string>()
      if (Array.isArray(r.ticket_ids) && r.ticket_ids.length > 0) {
        for (const id of r.ticket_ids) {
          if (id) next.add(id)
        }
      } else {
        for (const b of r.bookmarks ?? []) {
          const id = b.ticket?.id
          if (id) next.add(id)
        }
      }
      setBookmarkedIds(next)
    } catch {
      setBookmarkedIds(new Set())
    } finally {
      setLoading(false)
    }
  }, [user])

  const clearAll = useCallback(
    async (projectIds?: string[] | null, search?: string) => {
      if (!user) return
      await api.bookmarkClearAll({
        project_id: projectIds?.filter(Boolean).join(',') || undefined,
        search: search?.trim() || undefined,
      })
      await refresh()
      setListRevision((x) => x + 1)
      showNotification({ title: 'Закладки', message: 'Все закладки удалены', color: 'green' })
    },
    [refresh, user],
  )

  const clearMany = useCallback(
    async (ticketIds: string[]) => {
      if (!user) return
      const ids = ticketIds.filter((id) => Boolean(id)).filter((id, i, arr) => arr.indexOf(id) === i)
      if (ids.length === 0) return

      const next = await api.bookmarkClearMany(ids)
      if (!next?.deleted) {
        showNotification({
          title: 'Закладки',
          message: 'Не выбрано ни одной закладки для удаления',
          color: 'yellow',
        })
        return
      }

      await refresh()
      setListRevision((x) => x + 1)
      showNotification({ title: 'Закладки', message: 'Выбранные закладки удалены', color: 'green' })
    },
    [refresh, user],
  )

  useEffect(() => {
    if (!user?.id) {
      setBookmarkedIds(new Set())
      setLoading(false)
      return
    }
    let alive = true
    setLoading(true)
    void api
      .bookmarksList()
      .then((r) => {
        if (!alive) return
        const next = new Set<string>()
        for (const b of r.bookmarks ?? []) {
          const id = b.ticket?.id
          if (id) next.add(id)
        }
        setBookmarkedIds(next)
      })
      .catch(() => {
        if (alive) setBookmarkedIds(new Set())
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [user?.id])

  const isBookmarked = useCallback((ticketId: string) => bookmarkedIds.has(ticketId), [bookmarkedIds])

  const toggleBookmark = useCallback(
    async (ticketId: string) => {
      if (!user) return
      const was = idsRef.current.has(ticketId)
      const add = !was
      setBookmarkedIds((prev) => {
        const n = new Set(prev)
        if (add) n.add(ticketId)
        else n.delete(ticketId)
        return n
      })
      try {
        if (add) {
          await api.bookmarkAdd(ticketId)
          setListRevision((x) => x + 1)
          showNotification({
            title: 'Закладки',
            message: 'Обращение добавлено в закладки',
            color: 'green',
          })
        } else {
          await api.bookmarkRemove(ticketId)
          setListRevision((x) => x + 1)
          showNotification({
            title: 'Закладки',
            message: 'Удалено из закладок',
            color: 'gray',
          })
        }
      } catch (e) {
        setBookmarkedIds((prev) => {
          const n = new Set(prev)
          if (add) n.delete(ticketId)
          else n.add(ticketId)
          return n
        })
        const msg = e instanceof ApiError ? e.message : 'Не удалось изменить закладку'
        showNotification({ title: 'Закладки', message: msg, color: 'red' })
      }
    },
    [user],
  )

  const value = useMemo(
    () => ({
      bookmarkedIds,
      loading,
      listRevision,
      refresh,
      clearAll,
      clearMany,
      isBookmarked,
      toggleBookmark,
    }),
    [bookmarkedIds, loading, listRevision, refresh, clearAll, clearMany, isBookmarked, toggleBookmark],
  )

  return <BookmarksContext.Provider value={value}>{children}</BookmarksContext.Provider>
}

export function useBookmarks(): BookmarksContextValue {
  const ctx = useContext(BookmarksContext)
  if (!ctx) {
    throw new Error('useBookmarks must be used within BookmarksProvider')
  }
  return ctx
}
